import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  STATUS_LABEL,
  STATUS_LABEL_KEY,
  useArchiveLocation,
  useLocation,
  useUnarchiveLocation,
  useUpdateLocation,
} from '@/hooks/use-locations';
import { useT } from '@/i18n/use-t';
import { LocationTaxObjectPanel } from '@/pages/fiscal-cabinet/fiscal-record-panels';
import { AtbWeightDatabasePanel } from '@/pages/locations/atb-weight-database-panel';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

export function LocationDetailPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useLocation(id);
  const update = useUpdateLocation();
  const archive = useArchiveLocation();
  const unarchive = useUnarchiveLocation();

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [timezone, setTimezone] = useState('');
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    if (data === undefined) return;
    setName(data.name);
    setCity(data.city ?? '');
    setRegion(data.region ?? '');
    setAddressLine(data.addressLine ?? '');
    setPostalCode(data.postalCode ?? '');
    setCountryCode(data.countryCode ?? '');
    setTimezone(data.timezone ?? '');
    setBanner(null);
  }, [data?.id, data?.updatedAt]);

  if (isLoading)
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  if (data === undefined)
    return (
      <p className="text-sm text-muted-foreground">
        {t('locations.detail.notFound', 'Location not found.')}
      </p>
    );

  const isArchived = data.status === 'archived';

  async function onSave() {
    if (data === undefined) return;
    setBanner(null);
    try {
      await update.mutateAsync({
        id: data.id,
        body: {
          name: name.trim(),
          addressLine: addressLine.trim() === '' ? null : addressLine.trim(),
          city: city.trim() === '' ? null : city.trim(),
          region: region.trim() === '' ? null : region.trim(),
          postalCode: postalCode.trim() === '' ? null : postalCode.trim(),
          countryCode: countryCode.trim() === '' ? null : countryCode.trim().toUpperCase(),
          timezone: timezone.trim() === '' ? null : timezone.trim(),
        },
      });
    } catch (err) {
      setBanner(extractError(err) ?? t('locations.detail.saveFailed', 'Save failed.'));
    }
  }

  async function onToggleArchive() {
    if (data === undefined) return;
    setBanner(null);
    try {
      if (data.status === 'archived') {
        await unarchive.mutateAsync(data.id);
      } else {
        await archive.mutateAsync(data.id);
      }
    } catch (err) {
      setBanner(
        extractError(err) ?? t('locations.detail.statusChangeFailed', 'Status change failed.'),
      );
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <Link to="/locations" className="text-xs text-muted-foreground hover:underline">
          ← {t('locations.list.title', 'Locations')}
        </Link>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">{data.name}</h1>
          <Badge variant={isArchived ? 'destructive' : 'success'}>
            {t(STATUS_LABEL_KEY[data.status], STATUS_LABEL[data.status])}
          </Badge>
        </div>
        <p className="font-mono text-xs text-muted-foreground">{data.code}</p>
      </header>

      <section className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4">
        <div>
          <Label htmlFor="name">{t('locations.field.name', 'Name')}</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isArchived}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="countryCode">{t('locations.field.countryIso', 'Country (ISO-2)')}</Label>
          <Input
            id="countryCode"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={isArchived}
            maxLength={2}
            className="mt-1"
          />
        </div>

        <div className="col-span-2">
          <Label htmlFor="addressLine">{t('locations.field.address', 'Address')}</Label>
          <Input
            id="addressLine"
            value={addressLine}
            onChange={(e) => setAddressLine(e.target.value)}
            disabled={isArchived}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="city">{t('locations.field.city', 'City')}</Label>
          <Input
            id="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={isArchived}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="region">{t('locations.field.region', 'Region')}</Label>
          <Input
            id="region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={isArchived}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="postalCode">{t('locations.field.postalCode', 'Postal code')}</Label>
          <Input
            id="postalCode"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            disabled={isArchived}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="timezone">{t('locations.field.timezone', 'Timezone')}</Label>
          <Input
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            disabled={isArchived}
            className="mt-1"
          />
        </div>

        {banner !== null && (
          <p className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {banner}
          </p>
        )}

        <div className="col-span-2 flex gap-2">
          <Button onClick={onSave} disabled={isArchived || update.isPending}>
            {update.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
          <Button
            onClick={onToggleArchive}
            variant={isArchived ? 'outline' : 'destructive'}
            disabled={archive.isPending || unarchive.isPending}
          >
            {isArchived
              ? t('locations.detail.unarchive', 'Unarchive')
              : t('locations.detail.archive', 'Archive')}
          </Button>
        </div>
      </section>

      {/* PFC-10 — renders only for a fiscal-cabinet tenant and a user with fiscal.cabinet.write. */}
      <LocationTaxObjectPanel locationId={data.id} />

      {/* ATBL-449 — renders only for a weight-module tenant and a user with weight.read. */}
      <AtbWeightDatabasePanel locationId={data.id} />
    </div>
  );
}
